import { z } from "zod";
import {
  assembleCompletionNoteText,
  completionNoteSectionKeySchema,
} from "./completion-note";
import {
  completionSelectionSchema,
  COMPLETION_ITEMS,
  COMPLETION_OUTCOME_LABEL,
  completionItemSchema,
  type CompletionItem,
} from "./completion-picker";
import type { JournalDecision } from "./journal-decision";
import { journalWebdocStatusSchema } from "./journal-provenance";
import {
  assembleJournalText,
  journalSectionKeySchema,
} from "./meeting-journal";
import {
  meetingPrescriptionOrderSchema,
  ordinationProseBody,
} from "./prescription-cycle";
import { confirmPrescriptionChoiceSchema } from "./treatment-start";
import {
  plannedTreatmentChoiceSchema,
  TREATMENT_DECISION_OUTCOME_LABEL,
  treatmentDecisionOutcomeChoiceSchema,
  type PublishTreatmentDecision,
} from "./treatment-decision";

// TES-1061 (TES-1069 point 1, Emelie 2026-10-02) — the treatment decision's
// journal text.
//
// "När ingen komplettering behövs blir det normalt två anteckningar: vårdmötes- och beslutsanteckning, receptanteckning efter betalningen"
// With a completion, the decision goes in "Dokumentera kompletteringen och behandlingsbeslutet".
// TES-1221 — the prescription part no longer comes "after payment": since
// A-02 2026-10-08 its status is confirmed and journaled with the positive
// decision (`prescription` below), before payment.
// The decision is never a third note of its own, and an earlier note is
// never part of the next copy field (regression test 12).
//
// So the decision is journaled in exactly one of two notes, chosen by the
// server (TreatmentDecisionNoteService):
//   - `meeting`: the care meeting's note, as a new final version: the note
//     as it stands, with the meeting's own decision and then this one;
//   - `completion_note`: the open completion round's Kompletteringsanteckning
//     (TES-1060), and also whenever the meeting's note is locked history
//     because a completion note followed it.
// The decision's lines are TES-1058's (journalDecisionLines): the outcome as
// the label he picked, the preliminary sentence and the requested
// kompletteringar on a komplettering, his motivation and the care path
// verbatim. No prescription status: since TES-1080 that is the
// prescription note's.

// The fields of the decision that reach the note. The publish body carries
// them too, and the server compiles both from this shape, so the copied
// text and the confirmed text are the same function of the same input.
export const treatmentDecisionNoteDraftSchema = z.object({
  outcome: treatmentDecisionOutcomeChoiceSchema,
  // A decline's motivation, or the internal reason given with a new sample.
  reason: z.string().trim().max(2000).optional(),
  // A decline's "Fortsatt vårdväg".
  carePath: z.string().trim().max(2000).optional(),
  completion: completionSelectionSchema.optional(),
  // TES-1185 — a positive decision's prescription status, journaled with it
  // ("journalen dokumenterar … att recept faktiskt har utfärdats").
  prescription: confirmPrescriptionChoiceSchema.optional(),
  // TES-1185 — "Planen kvarstår" / "Ändra planerad läkemedelsbehandling";
  // on a change, the new order as prose.
  plannedTreatment: plannedTreatmentChoiceSchema.optional(),
  plannedOrderText: z.string().trim().max(400).optional(),
});
export type TreatmentDecisionNoteDraft = z.infer<
  typeof treatmentDecisionNoteDraftSchema
>;

/** TES-1185 — a changed plan's new order, as the note states it. */
export function plannedOrderProse(order: {
  medication: string;
  dose: string;
  frequency: string;
  duration?: string | null;
  // TES-1221 — a change may set the strength too.
  strength?: string | null;
}): string {
  return (
    ordinationProseBody({
      medication: order.medication,
      dose: order.dose,
      frequency: order.frequency,
      duration: order.duration ?? null,
      strength: order.strength ?? null,
    }) ?? ""
  );
}

/** The note's fields of a publish body. */
export function treatmentDecisionNoteDraftOf(
  decision: PublishTreatmentDecision,
): TreatmentDecisionNoteDraft {
  switch (decision.outcome) {
    case "approve":
      return {
        outcome: "approve",
        prescription: decision.prescription,
        ...(decision.plannedTreatment
          ? { plannedTreatment: decision.plannedTreatment }
          : {}),
        ...(decision.plannedTreatment?.choice === "changed"
          ? {
              plannedOrderText: plannedOrderProse({
                ...decision.approval,
                strength: decision.plannedTreatment.strength,
              }),
            }
          : {}),
      };
    case "completion":
      return {
        outcome: "completion",
        completion: decision.completion,
        ...(decision.reason ? { reason: decision.reason } : {}),
      };
    case "decline":
      return {
        outcome: "decline",
        reason: decision.reason,
        ...(decision.carePath ? { carePath: decision.carePath } : {}),
      };
  }
}

/** The decision as journal text (journalDecisionLines' input). */
export function treatmentJournalDecision(
  draft: TreatmentDecisionNoteDraft,
): JournalDecision {
  const asked = draft.outcome === "completion";
  const items = asked
    ? (draft.completion?.items ?? []).filter((i): i is CompletionItem =>
        (COMPLETION_ITEMS as readonly string[]).includes(i),
      )
    : [];
  return {
    outcome: TREATMENT_DECISION_OUTCOME_LABEL[draft.outcome],
    pending: asked,
    completion: asked
      ? {
          items,
          otherHeadings: draft.completion?.otherHeading
            ? [draft.completion.otherHeading]
            : [],
        }
      : null,
    motivation: draft.reason ?? null,
    carePath: draft.outcome === "decline" ? (draft.carePath ?? null) : null,
    prescription:
      draft.outcome === "approve" ? (draft.prescription ?? null) : null,
    plannedTreatment:
      draft.outcome !== "approve" || !draft.plannedTreatment
        ? null
        : draft.plannedTreatment.choice === "kept"
          ? { choice: "kept" }
          : {
              choice: "changed",
              reason: draft.plannedTreatment.reason ?? "",
              order: draft.plannedOrderText ?? null,
            },
  };
}

// The note the decision is written into, as it stands before the decision.
const meetingTargetSchema = z.object({
  kind: z.literal("meeting"),
  meetingId: z.string(),
  sections: z.array(
    z.object({ key: journalSectionKeySchema, content: z.string() }),
  ),
  order: meetingPrescriptionOrderSchema.nullable(),
  // The care meeting's own decision (meetingJournalDecision), already in
  // its note; the treatment decision's lines follow it.
  meetingDecision: z
    .object({
      outcome: z.string().nullable().optional(),
      pending: z.boolean().optional(),
      completion: z
        .object({
          items: z.array(completionItemSchema),
          otherHeadings: z.array(z.string()).optional(),
        })
        .nullable()
        .optional(),
    })
    .nullable(),
});
const completionTargetSchema = z.object({
  kind: z.literal("completion_note"),
  roundId: z.string(),
  noteDate: z.string(),
  sections: z.array(
    z.object({ key: completionNoteSectionKeySchema, content: z.string() }),
  ),
});
export const treatmentDecisionNoteTargetSchema = z.discriminatedUnion("kind", [
  meetingTargetSchema,
  completionTargetSchema,
]);
export type TreatmentDecisionNoteTarget = z.infer<
  typeof treatmentDecisionNoteTargetSchema
>;

// The two notes by their admin action names (TES-1069 point 1).
export const TREATMENT_DECISION_NOTE_TITLE: Record<
  TreatmentDecisionNoteTarget["kind"],
  string
> = {
  meeting: "Vårdmötes- och beslutsanteckning",
  completion_note: "Dokumentera kompletteringen och behandlingsbeslutet",
};

/**
 * The text the decision step copies and the server stores: the target note
 * with the decision's lines. Only that note; never an earlier one.
 */
export function assembleTreatmentDecisionNoteText(
  target: TreatmentDecisionNoteTarget,
  draft: TreatmentDecisionNoteDraft,
): string {
  const decision = treatmentJournalDecision(draft);
  if (target.kind === "completion_note") {
    return assembleCompletionNoteText(
      target.noteDate,
      target.sections,
      decision,
    );
  }
  // TES-1400 (Ö-526) — "Underlaget är tillräckligt – gå vidare är inte ett
  // fjärde utfall": a meeting that proceeded is approved by this decision,
  // so the approval is the note's one outcome. A completion or a decline
  // after it keeps the meeting's line.
  const meetingDecision =
    draft.outcome === "approve" &&
    target.meetingDecision?.outcome === COMPLETION_OUTCOME_LABEL.proceed
      ? null
      : (target.meetingDecision as JournalDecision | null);
  return assembleJournalText(target.sections, {
    order: target.order,
    decision: [meetingDecision, decision],
  });
}

// GET …/treatment-decision/note
export const treatmentDecisionNoteSchema = z.object({
  // Null when the patient has no note to write the decision into (no
  // documented care meeting and no completion round).
  target: treatmentDecisionNoteTargetSchema.nullable(),
  // The target's own Webdoc status.
  webdoc: journalWebdocStatusSchema.nullable(),
  // With a completion note: the earlier, finished note, read-only (never
  // copied). Null otherwise.
  earlierNote: z
    .object({
      title: z.string(),
      text: z.string(),
      locked: z.boolean(),
    })
    .nullable(),
});
export type TreatmentDecisionNote = z.infer<typeof treatmentDecisionNoteSchema>;

// POST …/treatment-decision/note/finalize — "Kopiera": the text stored as
// the target's newest final version. Its id is what the publish names.
export const treatmentDecisionNoteFinalizedSchema = z.object({
  revisionId: z.string().uuid(),
  version: z.number().int().positive(),
  text: z.string(),
});
export type TreatmentDecisionNoteFinalized = z.infer<
  typeof treatmentDecisionNoteFinalizedSchema
>;
